import { useState } from 'react';
import dayjs from 'dayjs';

import { apiFetch } from '@/api/client';
import { goodsReceiptsKey } from '@/api/goodsReceipts';
import { itemUnitsKey, itemVariantsKey, useItems } from '@/api/masters';
import { useApiMutation } from '@/api/mutations';
import { purchaseBillsKey } from '@/api/purchaseBills';
import { purchaseDeskKey } from '@/api/purchaseDesk';
import { purchaseOrdersKey, usePurchaseOrder } from '@/api/purchaseOrders';
import { DatePicker } from '@/components/DatePicker';
import {
  DocumentHeading,
  DocumentLines,
  LineName,
  type DocumentColumn,
} from '@/components/DocumentSheet';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Field, controlClass } from '@/components/Field';
import { Button, Card, SectionTitle } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { toDayjs, toIsoDate } from '@core/format/dates';
import type { PurchaseOrderLine } from '@core/api/purchaseOrders';
import { receivingDraft } from '@core/purchasing/order';
import type { Session } from '@core/session/types';
import { toBatchRequest, type IntakeDraft } from '@core/units/intake';
import { StockIntakePage } from '../units/StockIntakePage';

/**
 * Purchases › Receive (canvas "Purchase order flow", 2026-10-25) — "what came today?" against an
 * order: their challan and the day, then each design and size with what was ordered, what came
 * before, and a count for today that starts at everything still to come; beside it, today in total
 * and what the order becomes. The delivery goes up exactly as Book in stock sends it
 * (`receivingDraft` → `toBatchRequest`); anything not on the order goes through Book in stock itself.
 */
export function ReceiveScreen({
  session,
  orderId,
  onClose,
  onDone,
}: {
  session: Session;
  orderId: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const order = usePurchaseOrder(orderId);
  const today = dayjs().format('YYYY-MM-DD');
  const [counts, setCounts] = useState<Record<number, number>>({});
  const [challan, setChallan] = useState('');
  const [receivedOn, setReceivedOn] = useState(today);
  const [full, setFull] = useState<IntakeDraft | null>(null);
  const items = useItems(session.branch?.id, { includeInactive: true, pageSize: 200 });
  const fields = useAutoFocusFirstField<HTMLDivElement>();

  const receive = useApiMutation<
    { totalUnits: number; goodsReceiptNumber: string | null },
    IntakeDraft
  >({
    mutationFn: (draft) =>
      apiFetch('/api/item-unit/batch', { method: 'POST', body: toBatchRequest(draft) }),
    success: (result) =>
      `${result.totalUnits} garment${result.totalUnits === 1 ? '' : 's'} received` +
      (result.goodsReceiptNumber ? ` on ${result.goodsReceiptNumber}.` : '.'),
    invalidates: [
      purchaseOrdersKey,
      purchaseDeskKey,
      purchaseBillsKey,
      goodsReceiptsKey,
      itemUnitsKey,
      itemVariantsKey,
    ],
    onSuccess: onDone,
  });
  const busy = receive.isPending;

  if (!order.data) {
    return null;
  }

  const current = order.data;
  const pending = current.lines.filter((line) => line.pendingQuantity > 0);
  const countOf = (line: PurchaseOrderLine) => counts[line.id] ?? line.pendingQuantity;
  const coming = pending.reduce((sum, line) => sum + countOf(line), 0);
  const stillToCome = pending.reduce((sum, line) => sum + line.pendingQuantity, 0);

  // The order's own delivery, every line at what was counted today; lines with nothing today go.
  const draft = (): IntakeDraft => {
    const base = receivingDraft(current, receivedOn);

    return {
      ...base,
      supplierReference: challan,
      lines: base.lines
        .map((line) => ({
          ...line,
          quantity: counts[line.purchaseOrderLineId ?? 0] ?? line.quantity,
        }))
        .filter((line) => line.quantity > 0),
    };
  };

  if (full) {
    return (
      <StockIntakePage
        today={today}
        items={items.data?.items ?? []}
        branchId={session.branch?.id}
        branchName={session.branch?.name ?? 'this branch'}
        currency="₹"
        busy={busy}
        initial={full}
        against={{ orderNumber: current.documentNumber, supplierName: current.supplierName }}
        onClose={() => setFull(null)}
        onSubmit={(intake) => void receive.mutateAsync(intake).catch(() => undefined)}
      />
    );
  }

  const step = (line: PurchaseOrderLine, by: number) =>
    setCounts((was) => ({
      ...was,
      [line.id]: Math.min(line.pendingQuantity, Math.max(0, countOf(line) + by)),
    }));

  const columns: DocumentColumn<PurchaseOrderLine>[] = [
    {
      key: 'design',
      title: 'Design',
      render: (line) => <LineName name={line.itemName} note={line.itemCode} />,
    },
    { key: 'look', title: 'Colour · size', render: (line) => line.variantDisplayName },
    { key: 'ordered', title: 'Ordered', numeric: true, render: (line) => line.quantity },
    { key: 'before', title: 'Came before', numeric: true, render: (line) => line.receivedQuantity },
    {
      key: 'today',
      title: 'Came today',
      numeric: true,
      render: (line) => (
        <div className="inline-flex flex-col items-end">
          <div className="inline-flex items-center gap-1.5">
            <button
              type="button"
              aria-label={`One fewer ${line.variantDisplayName}`}
              disabled={busy || countOf(line) === 0}
              onClick={() => step(line, -1)}
              className="border-border-color hover:bg-light inline-flex size-8 items-center justify-center rounded-md border"
            >
              <i className="icon icon-minus" />
            </button>
            <input
              type="number"
              aria-label={`${line.itemName} ${line.variantDisplayName} came today`}
              onWheel={(event) => event.currentTarget.blur()}
              min={0}
              max={line.pendingQuantity}
              value={countOf(line)}
              disabled={busy}
              onChange={(event) =>
                setCounts((was) => ({
                  ...was,
                  [line.id]: Math.min(
                    line.pendingQuantity,
                    Math.max(0, Number(event.target.value) || 0),
                  ),
                }))
              }
              className={`${controlClass} max-w-16 text-center`}
            />
            <button
              type="button"
              aria-label={`One more ${line.variantDisplayName}`}
              disabled={busy || countOf(line) >= line.pendingQuantity}
              onClick={() => step(line, 1)}
              className="border-border-color hover:bg-light inline-flex size-8 items-center justify-center rounded-md border"
            >
              <i className="icon icon-plus" />
            </button>
          </div>
          <p className="text-default mt-0.5 mb-0 text-[11px]">
            of {line.pendingQuantity} still to come
          </p>
        </div>
      ),
    },
  ];

  const after = stillToCome - coming;

  return (
    <>
      <DocumentHeading
        trail={current.documentNumber}
        current="Receive"
        title={`What came today from ${current.supplierName}?`}
        subtitle="Each count starts at everything still to come. Lower it for what did not arrive."
        onBack={onClose}
        actions={
          <Button icon="icon icon-arrow-left" shortcut="back" disabled={busy} onClick={onClose}>
            Back
          </Button>
        }
      />

      <div ref={fields} {...enterKeyScope} className="grid grid-cols-12 gap-4">
        <div className="col-span-12 xl:col-span-8">
          <Card>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Their challan number">
                <input
                  value={challan}
                  disabled={busy}
                  onChange={(event) => setChallan(event.target.value)}
                  className={controlClass}
                />
              </Field>
              <Field label="Received on">
                <DatePicker
                  className="w-full"
                  value={toDayjs(receivedOn) ?? null}
                  allowClear={false}
                  disabled={busy}
                  onChange={(value) => setReceivedOn(toIsoDate(value) ?? receivedOn)}
                />
              </Field>
            </div>

            <div className="border-border-color mt-5 border-t pt-5">
              <DocumentLines columns={columns} rows={pending} rowKey={(line) => line.id} />
              <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                Something came that is not on this order?{' '}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setFull(draft())}
                  className="text-primary cursor-pointer font-semibold hover:underline"
                >
                  Book it in through Stock in
                </button>
              </p>
            </div>
          </Card>
        </div>

        <div className="col-span-12 xl:col-span-4">
          <Card>
            <SectionTitle>Today</SectionTitle>
            <div className="flex flex-col gap-2 text-[length:var(--scale-font-size)]">
              <p className="mb-0 flex justify-between">
                <span className="text-default">Garments coming in</span>
                <span className="font-mono-nums font-semibold text-gray-900">{coming}</span>
              </p>
              <p className="mb-0 flex justify-between">
                <span className="text-default">Still to come after this</span>
                <span className="font-mono-nums font-semibold text-gray-900">{after}</span>
              </p>
              <p className="border-border-color mb-0 flex items-center justify-between border-t pt-2">
                <span className="font-bold text-gray-900">Order becomes</span>
                <StatusBadge tone={after === 0 ? 'positive' : 'warning'}>
                  {after === 0 ? 'Received' : 'Part received'}
                </StatusBadge>
              </p>
            </div>
            <p className="text-default mt-3 mb-0 text-[length:var(--scale-list-meta)]">
              Each garment gets its own tag code from its design and size as it is booked in.
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <Button
                variant="primary"
                icon="icon icon-package-check"
                data-enter-key-commit
                shortcut="save"
                disabled={busy || coming === 0}
                onClick={() => void receive.mutateAsync(draft()).catch(() => undefined)}
              >
                {busy ? 'Booking in…' : `Book in ${coming} garment${coming === 1 ? '' : 's'}`}
              </Button>
              <Button variant="secondary" disabled={busy} onClick={onClose}>
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
